package er.prateek.crossly.game.crossword

/** One key on the rack: a letter, and whether the board has already claimed it. */
data class RackTile(val letter: Char, val spent: Boolean)

private val Alphabet = ('A'..'Z').toList()

/**
 * Works out which letters to put in front of the player.
 *
 * A cell only counts as done when it holds the *right* letter — a wrong guess
 * must never make the letter it displaced disappear from the rack, or the player
 * is left with no way to type the correction.
 */
object LetterSupply {

    /** Distinct letters still missing anywhere in the grid, alphabetical. */
    fun relevantLetters(engine: CrosswordEngine): List<Char> =
        engine.cells
            .filter { !it.isBlack && !it.isResolved }
            .mapNotNull { it.solution }
            .distinct()
            .sorted()

    /**
     * Every distinct letter in the puzzle's solution, alphabetical -- resolved or not. Backs
     * [er.prateek.crossly.game.crossword.KeyboardMode.THIS_PUZZLE], where the rack is meant to
     * stay put rather than shrink as the player fills the grid in.
     */
    fun allPuzzleLetters(engine: CrosswordEngine): List<Char> =
        engine.cells
            .filter { !it.isBlack }
            .mapNotNull { it.solution }
            .distinct()
            .sorted()

    /**
     * The rack for one word: every letter of [answer] plus a few letters that
     * aren't in it, so the word can't simply be read off the tiles.
     *
     * Seeded from the answer itself — the order has to survive recomposition and
     * app restarts, otherwise the tiles jump around under the player's thumb.
     */
    fun wordRack(answer: String, decoyCount: Int = defaultDecoyCount(answer)): List<Char> {
        val letters = answer.uppercase().filter { it.isLetter() }.toList()
        if (letters.isEmpty()) return emptyList()

        val random = PuzzleRandom(answer.stableSeed())
        val pool = Alphabet.filterNot { it in letters }
        val decoys = random.shuffled(pool).take(decoyCount.coerceAtMost(pool.size))
        return random.shuffled(letters + decoys)
    }

    /** A short word gets one decoy, a long one up to three. */
    fun defaultDecoyCount(answer: String): Int = (answer.length / 3).coerceIn(1, 3)

    /**
     * Marks rack tiles against the letters already correctly placed in the word,
     * one tile per placed letter, so duplicates are consumed one at a time.
     */
    fun tilesFor(rack: List<Char>, placed: List<Char>): List<RackTile> {
        val remaining = placed.groupingBy { it }.eachCount().toMutableMap()
        return rack.map { letter ->
            val left = remaining[letter] ?: 0
            if (left > 0) {
                remaining[letter] = left - 1
                RackTile(letter, spent = true)
            } else {
                RackTile(letter, spent = false)
            }
        }
    }

    /** Letters of the word under the cursor that the player has already got right. */
    fun placedLettersInCurrentWord(engine: CrosswordEngine, wordCells: Collection<CellPos>): List<Char> =
        wordCells.mapNotNull { pos -> engine.cellAt(pos)?.takeIf { it.isResolved }?.solution }
}
